import { useEffect, useRef, useState } from 'react';
import { Box, Chip } from '@mui/material';
import _ from 'lodash';
import classNames from 'classnames';
import { Media } from '../../../../generated/graphql';
import { PostMedia } from './PostMedia';
import { splitTextByChannelMention } from '../utils/channelMention';
import { mdToHTML } from '../utils/mdToHTML';

type Props = {
  text: string;
  media: Media[];
  isPostPage: boolean;
  isPinned?: boolean;
};

const CLAMP_TEXT_SIZE_WITH_ATTACHMENT = 270;
const CLAMP_TEXT_SIZE_WITHOUT_ATTACHMENT = 500;

const mentionStyle = {
  color: 'var(--primary-P-80, #5F1ECC)',
  fontFamily: 'Visuelt, sans-serif',
  fontSize: '16px',
  fontStyle: 'normal',
  fontWeight: 400,
  lineHeight: '24px',
};

export const PostContent = ({
  text,
  media,
  isPostPage,
  isPinned = false,
}: Props) => {
  const textRef = useRef<HTMLDivElement>();
  const textContainerRef = useRef<HTMLDivElement>(null);
  const hasAttachments = !_.isEmpty(media);
  const canwrappedText =
    (hasAttachments && text.length > CLAMP_TEXT_SIZE_WITH_ATTACHMENT) ||
    text.length > CLAMP_TEXT_SIZE_WITHOUT_ATTACHMENT;
  const [wrappedText, setWrappedText] = useState(canwrappedText);
  const [fullHeight, setFullHeight] = useState<number | null>(null);
  const clampHeight = (hasAttachments ? 3 : 7) * 24; // line-height is 24px

  const onWrap = (ev: React.MouseEvent<HTMLElement>) => {
    ev.stopPropagation();
    setWrappedText((prev) => !prev);
  };

  const onContentClickCapture = (ev: React.MouseEvent<HTMLSpanElement>) => {
    const target = ev.target as HTMLElement;
    if (target.closest('a')) {
      ev.stopPropagation();
    }
  };

  useEffect(() => {
    const node = textContainerRef.current;
    if (!node) return;
    const prevMax = node.style.maxHeight;
    node.style.maxHeight = 'none';
    const height = node.scrollHeight;
    node.style.maxHeight = prevMax;
    setFullHeight(height);
  }, [text, media, hasAttachments]);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: '1rem',
      }}
      ref={textRef}
    >
      <div
        ref={textContainerRef}
        className={classNames('relative', {
          'overflow-hidden': true,
        })}
        style={{
          maxHeight: wrappedText
            ? `${clampHeight}px`
            : fullHeight
            ? `${fullHeight}px`
            : 'none',
          transition: 'max-height 220ms ease',
        }}
      >
        {splitTextByChannelMention(text).map((part, index) =>
          part.isChannelMention ? (
            <span key={index} style={mentionStyle}>
              {part.text}
            </span>
          ) : (
            <span
              key={index}
              onClickCapture={onContentClickCapture}
              style={{
                color: 'var(--bw-black-B-100, #000)',
                fontFamily: 'Visuelt, sans-serif',
                fontSize: '16px',
                fontStyle: 'normal',
                fontWeight: 400,
                lineHeight: '24px',
                whiteSpace: 'pre-wrap',
              }}
              dangerouslySetInnerHTML={{ __html: mdToHTML(part.text) }}
            />
          ),
        )}
      </div>
      {canwrappedText && (
        <div className="mt-2 flex justify-end">
          <button
            type="button"
            className="cursor-pointer text-purple-1 font-extrabold"
            onClick={onWrap}
            data-testid="wrap-action"
          >
            {wrappedText ? 'Show More' : 'Show Less'}
          </button>
        </div>
      )}
      {hasAttachments && <PostMedia media={media} isPostPage={isPostPage} />}
    </Box>
  );
};
